import React, { useEffect, useState, useRef } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import Input from "../Input";
import UserPicker, { UserPickerValue } from "../UserPicker";
import CommentSection from "../CommentSection";
import { useDirtyContext } from "../../contexts/DirtyContext";
import { createTask, getTaskById, updateTask } from "../../services/taskService";
import { v4 as uuidv4 } from "uuid";
import ConcurrencyConflictModal, { ConcurrencyConflict, ResolvedConflicts } from "../ConcurrencyConflictModal";
import {
    EntityParentType,
    INITIAL_TASK,
    Task,
    TaskPriority,
    TaskStatus,
    TASK_PRIORITY_LABELS,
    TASK_STATUS_LABELS,
} from "../../types/Task";
import Button from "../Button";
import { EditPanels, FormGrid, FormGridItem, Panel } from "../record";
import AskFilledNote from "../assistant/AskFilledNote";
import classes from "./taskFormModal.module.scss";

interface TaskFormModalProps {
    show: boolean;
    onClose: () => void;
    onSaved?: (task: Task) => void;
    taskId?: number | null;
    parentType?: EntityParentType | null;
    parentId?: number | null;
    initialData?: Partial<Task> | null;
    /** Fields Ask filled in (D9), shown at the top for the person to check. */
    askFilled?: string[];
}

interface ValidationErrors {
    name?: string;
    status?: string;
    priority?: string;
}

const TaskFormModal: React.FC<TaskFormModalProps> = ({
    show,
    onClose,
    onSaved,
    taskId,
    parentType,
    parentId,
    initialData,
    askFilled,
}) => {
    const { setIsDirty } = useDirtyContext();
    const [task, setTask] = useState<Task>({ ...INITIAL_TASK });
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [errors, setErrors] = useState<ValidationErrors>({});
    const isEditMode = !!taskId && taskId > 0;

    // Snapshot of task at edit-load time — feeds BE auto-merge + modal Original column.
    const originalRef = useRef<Task | null>(null);
    const [conflicts, setConflicts] = useState<ConcurrencyConflict[]>([]);

    useEffect(() => {
        if (!show) return;
        setErrors({});
        if (isEditMode) {
            setIsLoading(true);
            getTaskById(taskId!).then((response) => {
                if (response?.status === 200 && response.body) {
                    const data = (response.body?.data ?? response.body) as Task;
                    const fresh = { ...INITIAL_TASK, ...data, subtasks: data.subtasks ?? [] };
                    setTask(fresh);
                    originalRef.current = JSON.parse(JSON.stringify(fresh));
                }
                setIsLoading(false);
            });
        } else if (initialData) {
            setTask({
                ...INITIAL_TASK,
                ...initialData,
                parentType: parentType ?? initialData.parentType ?? null,
                parentId: parentId ?? initialData.parentId ?? null,
                subtasks: initialData.subtasks ?? [],
            } as Task);
            setIsSaving(false);
        } else {
            setTask({
                ...INITIAL_TASK,
                parentType: parentType ?? null,
                parentId: parentId ?? null,
                subtasks: [],
            });
            setIsSaving(false);
        }
    }, [show, taskId, parentType, parentId, isEditMode, initialData]);

    const handleChange = (name: string, value: string | boolean | object) => {
        setTask((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: undefined }));
        setIsDirty(true);
    };

    const handleAssigneeChange = (user: UserPickerValue | null) => {
        setTask((prev) => ({
            ...prev,
            assignedUser: user
                ? {
                    userId: user.userId,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    fullName: user.fullName,
                    photo: user.photo ?? null,
                }
                : null,
        }));
        setIsDirty(true);
    };

    // Subtask handlers
    const visibleSubtasks = (task.subtasks ?? []).filter((s) => !s.deletedAt);

    const handleSubtaskFieldChange = (index: number, field: keyof Task, value: any) => {
        setTask((prev) => {
            const subtasks = [...(prev.subtasks ?? [])];
            subtasks[index] = { ...subtasks[index], [field]: value };
            return { ...prev, subtasks };
        });
        setIsDirty(true);
    };

    const handleSubtaskAssigneeChange = (index: number, user: UserPickerValue | null) => {
        setTask((prev) => {
            const subtasks = [...(prev.subtasks ?? [])];
            subtasks[index] = {
                ...subtasks[index],
                assignedUser: user
                    ? {
                        userId: user.userId,
                        firstName: user.firstName,
                        lastName: user.lastName,
                        fullName: user.fullName,
                        photo: user.photo ?? null,
                    }
                    : null,
            };
            return { ...prev, subtasks };
        });
        setIsDirty(true);
    };

    const handleAddSubtask = () => {
        setTask((prev) => ({
            ...prev,
            subtasks: [
                ...(prev.subtasks ?? []),
                { ...INITIAL_TASK, taskId: 0, name: null, status: TaskStatus.NotStarted, priority: TaskPriority.Normal },
            ],
        }));
        setIsDirty(true);
    };

    const handleRemoveSubtask = (index: number) => {
        setTask((prev) => {
            const subtasks = [...(prev.subtasks ?? [])];
            const target = subtasks[index];
            if (!target) return prev;
            if (!target.taskId || target.taskId <= 0) {
                subtasks.splice(index, 1);
            } else {
                subtasks[index] = { ...target, deletedAt: new Date().toISOString() };
            }
            return { ...prev, subtasks };
        });
        setIsDirty(true);
    };

    const handleClose = () => {
        setIsDirty(false);
        setErrors({});
        onClose();
    };

    const validate = (): boolean => {
        const newErrors: ValidationErrors = {};
        if (!task.name || task.name.trim() === "") {
            newErrors.name = "Task name is required";
        }
        if (!task.status) {
            newErrors.status = "Status is required";
        }
        if (!task.priority) {
            newErrors.priority = "Priority is required";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const buildTaskPayload = (forceOverwrite = false, overwriteReason?: string, clientMutationId?: string): any => {
        const payload: any = {
            name: task.name,
            description: task.description,
            status: task.status,
            priority: task.priority,
            assignedUser: task.assignedUser,
            startAt: task.startAt,
            endAt: task.endAt,
            parentType: task.parentType,
            parentId: task.parentId,
            parentTaskId: task.parentTaskId,
            subtasks: task.subtasks,
        };
        if (isEditMode) {
            payload.version = (task as any).version;
            payload.clientMutationId = clientMutationId ?? uuidv4();
            if (forceOverwrite) payload.forceOverwrite = true;
            if (overwriteReason) payload.overwriteReason = overwriteReason;
            if (originalRef.current) payload.originalSnapshot = originalRef.current;
        }
        return payload;
    };

    /** Conflict-modal lookups: parent task, subtasks, taskDocuments. */
    const lookupDraftRow = (entityName: string, id: number): any | undefined => {
        if (entityName === "task" && id === task.taskId) return task;
        if (entityName === "task") return (task.subtasks ?? []).find(s => s.taskId === id);
        if (entityName === "taskDocument") return (task.taskDocuments ?? []).find((d: any) => d.documentId === id);
        return undefined;
    };
    const lookupOriginalRow = (entityName: string, id: number): any | undefined => {
        const original = originalRef.current;
        if (!original) return undefined;
        if (entityName === "task" && id === original.taskId) return original;
        if (entityName === "task") return (original.subtasks ?? []).find(s => s.taskId === id);
        if (entityName === "taskDocument") return (original.taskDocuments ?? []).find((d: any) => d.documentId === id);
        return undefined;
    };

    const handleConflictResolve = async (resolved: ResolvedConflicts) => {
        let nextDraft: Task = JSON.parse(JSON.stringify(task));
        const original = originalRef.current;

        const mergeFields = (draft: any, canonical: any, orig: any): any => {
            const merged: any = { ...draft };
            const skip = new Set(["version", "createdAt", "updatedAt", "deletedAt"]);
            const allKeys = Array.from(new Set<string>([...Object.keys(canonical ?? {}), ...Object.keys(orig ?? {})]));
            for (const field of allKeys) {
                if (skip.has(field)) continue;
                const userChanged = JSON.stringify(draft?.[field]) !== JSON.stringify(orig?.[field]);
                if (!userChanged) merged[field] = canonical?.[field];
            }
            return merged;
        };

        for (const [key, decision] of Array.from(resolved.decisions.entries())) {
            const canonical = resolved.canonicalById.get(key);
            if (!canonical) continue;
            const [entityName, idStr] = key.split(":");
            const id = parseInt(idStr, 10);

            if (entityName === "task" && id === task.taskId) {
                if (decision.kind === "accept-theirs") {
                    nextDraft = { ...nextDraft, ...canonical };
                } else if (decision.kind === "accept-both" && original) {
                    nextDraft = mergeFields(nextDraft, canonical, original);
                }
                continue;
            }

            if (entityName === "task") {
                const arr = nextDraft.subtasks ?? [];
                const idx = arr.findIndex((s: any) => s.taskId === id);
                if (idx < 0) continue;
                if (decision.kind === "accept-theirs") arr[idx] = canonical;
                else if (decision.kind === "accept-both") arr[idx] = mergeFields(arr[idx], canonical, lookupOriginalRow(entityName, id));
                nextDraft.subtasks = arr;
            } else if (entityName === "taskDocument") {
                const arr = (nextDraft as any).taskDocuments ?? [];
                const idx = arr.findIndex((d: any) => d.documentId === id);
                if (idx < 0) continue;
                if (decision.kind === "accept-theirs") arr[idx] = canonical;
                else if (decision.kind === "accept-both") arr[idx] = mergeFields(arr[idx], canonical, lookupOriginalRow(entityName, id));
                (nextDraft as any).taskDocuments = arr;
            }
        }

        setTask(nextDraft);
        setConflicts([]);

        // Replay save with forceOverwrite.
        setIsSaving(true);
        try {
            const taskData = buildTaskPayload(true, resolved.overwriteReason);
            // Apply nextDraft on top so resolutions land in the payload.
            Object.assign(taskData, nextDraft, {
                version: (nextDraft as any).version ?? taskData.version,
                clientMutationId: taskData.clientMutationId,
                forceOverwrite: true,
                overwriteReason: resolved.overwriteReason,
                originalSnapshot: taskData.originalSnapshot,
            });
            const formData = new FormData();
            formData.append("taskData", JSON.stringify(taskData));
            const response = await updateTask(taskId!, formData);
            if (response?.status !== 200) {
                toast.error("Failed to save resolved task");
            } else {
                const saved = (response.body?.data ?? response.body) as Task;
                toast.success("Task saved (conflict resolved).");
                setIsDirty(false);
                onSaved?.(saved);
                onClose();
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to save resolved task");
        } finally {
            setIsSaving(false);
        }
    };

    const handleConflictCancel = () => {
        setConflicts([]);
    };

    const handleSave = async () => {
        if (!validate()) return;
        setIsSaving(true);
        try {
            const taskData = buildTaskPayload();

            const formData = new FormData();
            formData.append("taskData", JSON.stringify(taskData));

            let response;
            if (isEditMode) {
                response = await updateTask(taskId!, formData);
            } else {
                response = await createTask(formData);
            }

            if (isEditMode && response?.status === 409 && response.body?.error === "CONCURRENCY_CONFLICT") {
                setConflicts(response.body.conflicts ?? []);
                setIsSaving(false);
                return;
            }
            if (response?.status !== 200 && response?.status !== 201) {
                toast.error(response?.body?.message ?? `Failed to ${isEditMode ? "update" : "create"} task`);
                setIsSaving(false);
                return;
            }
            const saved = (response.body?.data ?? response.body) as Task;
            toast.success(`Task ${isEditMode ? "updated" : "created"} successfully`);
            setIsDirty(false);
            onSaved?.(saved);
            onClose();
        } catch (err) {
            console.error(err);
            toast.error(`Failed to ${isEditMode ? "update" : "create"} task`);
        } finally {
            setIsSaving(false);
        }
    };

    const statusOptions = Object.values(TaskStatus).map((s) => ({
        value: s,
        label: TASK_STATUS_LABELS[s],
    }));
    const priorityOptions = Object.values(TaskPriority).map((p) => ({
        value: p,
        label: TASK_PRIORITY_LABELS[p],
    }));

    return (
        <>
        <ConcurrencyConflictModal
            show={conflicts.length > 0}
            conflicts={conflicts}
            userDraftLookup={lookupDraftRow}
            originalLookup={lookupOriginalRow}
            onResolve={handleConflictResolve}
            onCancel={handleConflictCancel}
        />
        <Modal show={show} onHide={handleClose} size="lg" fullscreen="sm-down">
            <Modal.Header closeButton>
                <Modal.Title>{isEditMode ? "Edit task" : "New task"}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
                {askFilled && <AskFilledNote fields={askFilled} />}
                {isLoading ? (
                    <div className="text-muted">Loading the task…</div>
                ) : (
                    <EditPanels>
                        <FormGrid>
                            <FormGridItem wide>
                                <Input
                                    type="text"
                                    label="Task name"
                                    id="name"
                                    value={task.name ?? ""}
                                    onChange={handleChange}
                                    required
                                    isInvalid={!!errors.name}
                                    errorMessage={errors.name}
                                    tooltip="What has to be done, in a few words"
                                />
                            </FormGridItem>
                            <FormGridItem>
                                <Input
                                    type="select"
                                    label="Status"
                                    id="status"
                                    value={task.status ?? ""}
                                    onChange={handleChange}
                                    options={statusOptions}
                                    required
                                    isInvalid={!!errors.status}
                                    errorMessage={errors.status}
                                />
                            </FormGridItem>
                            <FormGridItem>
                                <Input
                                    type="select"
                                    label="Priority"
                                    id="priority"
                                    value={task.priority ?? ""}
                                    onChange={handleChange}
                                    options={priorityOptions}
                                    required
                                    isInvalid={!!errors.priority}
                                    errorMessage={errors.priority}
                                />
                            </FormGridItem>
                            <FormGridItem>
                                <UserPicker
                                    label="Assignee"
                                    id="assignedUserId"
                                    value={task.assignedUser}
                                    onChange={handleAssigneeChange}
                                    placeholder="Assignee"
                                />
                            </FormGridItem>
                            <FormGridItem>
                                <Input
                                    type="date"
                                    label="Start date"
                                    id="startAt"
                                    value={task.startAt ? String(task.startAt).substring(0, 10) : ""}
                                    onChange={handleChange}
                                    className="form-control"
                                    tooltip="When work should begin"
                                />
                            </FormGridItem>
                            <FormGridItem>
                                <Input
                                    type="date"
                                    label="Due date"
                                    id="endAt"
                                    value={task.endAt ? String(task.endAt).substring(0, 10) : ""}
                                    onChange={handleChange}
                                    className="form-control"
                                    tooltip="When it has to be done"
                                />
                            </FormGridItem>
                            <FormGridItem wide>
                                <Input
                                    type="textarea"
                                    label="Description"
                                    id="description"
                                    value={task.description ?? ""}
                                    onChange={handleChange}
                                    rows={3}
                                    className="form-control"
                                />
                            </FormGridItem>
                        </FormGrid>

                        <Panel
                            title="Subtasks"
                            count={visibleSubtasks.length || null}
                            summary={visibleSubtasks.length ? "Saved with the task" : undefined}
                            action={{ label: "Add subtask", title: "Add a step to this task", icon: "plus-lg", onClick: handleAddSubtask }}
                            isEmpty={visibleSubtasks.length === 0}
                            emptyText="No subtasks yet."
                        >
                            {(task.subtasks ?? []).map((sub, index) => {
                                if (sub.deletedAt) return null;
                                return (
                                    <div key={sub.taskId || `new-${index}`} className={classes.subtaskCard}>
                                        <FormGrid>
                                            <FormGridItem>
                                                <Input
                                                    type="text"
                                                    label="Subtask"
                                                    id={`sub-name-${index}`}
                                                    value={sub.name ?? ""}
                                                    onChange={(_, v) => handleSubtaskFieldChange(index, "name", v)}
                                                />
                                            </FormGridItem>
                                            <FormGridItem>
                                                <Input
                                                    type="select"
                                                    label="Priority"
                                                    id={`sub-priority-${index}`}
                                                    value={sub.priority ?? ""}
                                                    onChange={(_, v) => handleSubtaskFieldChange(index, "priority", v)}
                                                    options={priorityOptions}
                                                />
                                            </FormGridItem>
                                            <FormGridItem>
                                                <UserPicker
                                                    label="Assignee"
                                                    id={`sub-assignee-${index}`}
                                                    value={sub.assignedUser}
                                                    onChange={(u) => handleSubtaskAssigneeChange(index, u)}
                                                    placeholder="Assignee"
                                                />
                                            </FormGridItem>
                                            <FormGridItem wide>
                                                <Input
                                                    type="textarea"
                                                    label="Notes"
                                                    id={`sub-description-${index}`}
                                                    value={sub.description ?? ""}
                                                    onChange={(_, v) => handleSubtaskFieldChange(index, "description", v)}
                                                    rows={2}
                                                    className="form-control"
                                                />
                                            </FormGridItem>
                                            <FormGridItem wide>
                                                <div className="d-flex justify-content-end">
                                                    <Button type="button" size="xs" variant="danger" icon="trash"
                                                        title="Remove this subtask (on Save)" onClick={() => handleRemoveSubtask(index)}>
                                                        Remove
                                                    </Button>
                                                </div>
                                            </FormGridItem>
                                        </FormGrid>
                                    </div>
                                );
                            })}
                        </Panel>

                        {/* Comments — only available once the task is persisted. */}
                        {isEditMode && task.taskId > 0 && (
                            <Panel title="Discussion">
                                <CommentSection parentType="task" parentId={task.taskId} />
                            </Panel>
                        )}
                    </EditPanels>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button type="button" variant="ghost" onClick={handleClose} disabled={isSaving} title="Close without saving">
                    Cancel
                </Button>
                <Button
                    type="button"
                    variant="primary"
                    icon="check2"
                    onClick={handleSave}
                    disabled={isSaving || isLoading}
                    loading={isSaving}
                    loadingText="Saving…"
                    title="Save the task"
                >
                    {isEditMode ? "Save changes" : "Create task"}
                </Button>
            </Modal.Footer>
        </Modal>
        </>
    );
};

export default TaskFormModal;
